import CardStack, { type StackCard } from '../components/CardStack';
import { showConfirm } from '../components/ConfirmDialog';
import PasswordInput from '../components/PasswordInput';
import { useState, useEffect, useCallback, useMemo, Fragment, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  apiGetAppUsers, apiCreateAppUser, apiUpdateAppUser, apiDeleteAppUser,
  apiGetBands, apiSetBandMember, apiRemoveBandMember,
  apiCreateInvitation, apiGetInvitations, apiRevokeInvitation, apiResendInvitation,
  apiGetAuditLog, apiRevokeUserSessions, apiUnlockUser, apiGetAllPeople, apiSetMemberPassword,
  type AppUser, type BandDTO, type BandRole, type GlobalRole,
  type InvitationDTO, type InvitationDelivery, type AuditEntry, type PersonBrief, ApiError,
} from '../api';
import { reportSaveError } from '../lib/saveStatus';
import { useWriteGate } from '../lib/writeGate';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

// The roles OFFERED in the selector. `crew` is absent on purpose: it is the
// pre-rename spelling that the DB still accepts for rollback safety — we never
// CREATE new legacy rows, but a payload carrying one is handled (see ROLE_HELP).
const BAND_ROLES: BandRole[] = ['owner', 'editor', 'tech', 'viewer'];

const ROLE_HELP: Record<BandRole, string> = {
  owner: 'users.roleOwner',
  editor: 'users.roleEditor',
  tech: 'users.roleTech',
  // `crew` = the PRE-RENAME spelling of the same role. The DB still accepts it
  // (safe rollback), so a payload can carry it: it must render as Technician, not
  // as a raw i18n key.
  crew: 'users.roleTech',
  viewer: 'users.roleViewer',
};

type Tab = 'users' | 'invitations' | 'audit';
type SortKey = 'username' | 'last_login' | 'created';

/** Password-strength meter for the reset modal (0..4). */
function strength(pw: string): { score: number; label: string } {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(pw)).length;
  if (classes >= 3) score++;
  if (classes >= 4 && pw.length >= 10) score++;
  const labels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
  return { score: Math.min(score, 4), label: labels[Math.min(score, 4)] };
}
// LOT U1b — les barres de force portent une couleur de DONNÉE (dégradé de sévérité,
// graphique, jamais du texte) ; le TEXTE du libellé, lui, suit les jetons pour
// rester ≥4.5:1 dans les deux thèmes (`#6bd07b` sur --card = 2.2:1 en clair).
const STRENGTH_COLORS = ['#e07474', '#e07474', '#f0a020', '#6bd07b', '#2ecc71'];
const STRENGTH_INK = ['var(--danger)', 'var(--danger)', 'var(--warning)', 'var(--success)', 'var(--success)'];

export default function UsersPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { user } = useAuth();
  const { t } = useTranslation();
  // Platform admin, OR the read-only showcase (demo) account — the demo browses
  // this page with SAMPLE data (server fixtures) and cannot mutate anything.
  const isSuper = (user as { global_role?: string; is_showcase?: boolean } | null)?.global_role === 'superadmin'
    || !!(user as { is_showcase?: boolean } | null)?.is_showcase;
  const showcase = !!user?.is_showcase;
  // Capability-aware write gate (lot A2). `/api/users` (userRouter) is mounted
  // AFTER the server's global read-only guard (server/index.js:519-525; router
  // at :571). This page already hides itself from a non-admin; the gate freezes
  // the showcase demo, which does render it with sample data.
  const writeGate = useWriteGate();
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;

  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<AppUser[]>([]);
  const [bands, setBands] = useState<BandDTO[]>([]);
  const [invitations, setInvitations] = useState<InvitationDTO[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [people, setPeople] = useState<PersonBrief[]>([]);
  // Team registry (EVERY team_member, not just the login accounts) — lot U.
  const [regSearch, setRegSearch] = useState('');
  const [regBand, setRegBand] = useState('');
  const [regSort, setRegSort] = useState<'name' | 'category'>('name');
  const [regNoAccountOnly, setRegNoAccountOnly] = useState(false);
  // Person whose roster is AMBIGUOUS (in several bands): we ask which band
  // before building the invite, never guess.
  const [regInviteFor, setRegInviteFor] = useState<string | null>(null);
  const [regInviteBand, setRegInviteBand] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // New-user form
  const [nu, setNu] = useState({ username: '', password: '', email: '', display_name: '', global_role: 'user' as GlobalRole });
  // Invitation form
  const [inv, setInv] = useState({ email: '', band_id: '', band_role: 'viewer' as BandRole });
  // Durable invite confirmation: the link + delivery summary of the LAST
  // created / re-sent invitation. Kept in state so it survives a reload (the
  // link no longer "flies away" — it is also persisted server-side and shown
  // on the pending row, but this box gives immediate feedback).
  const [lastInvite, setLastInvite] = useState<{
    email: string;
    link: string;
    delivery?: InvitationDelivery;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // Filters + sorting (accounts tab)
  const [search, setSearch] = useState('');
  const [fRole, setFRole] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fBand, setFBand] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('username');

  // Audit
  const [auditAction, setAuditAction] = useState('');
  const [auditActor, setAuditActor] = useState('');
  const [auditOffset, setAuditOffset] = useState(0);
  const AUDIT_PAGE = 100;

  // Password reset modal
  const [pwModal, setPwModal] = useState<AppUser | null>(null);
  const [pwVal, setPwVal] = useState('');

  // "Set password" modal (phase alpha): give a Team member WITHOUT an account
  // a password directly — the server CREATES the account. mcp defaults to true
  // (must_change_password=1), the superadmin may untick it for alpha testing.
  const [spPerson, setSpPerson] = useState<PersonBrief | null>(null);
  const [spVal, setSpVal] = useState('');
  const [spMcp, setSpMcp] = useState(true);

  // Edit-account modal (username / email / display name)
  const [editModal, setEditModal] = useState<AppUser | null>(null);
  const [ev, setEv] = useState({ username: '', email: '', display_name: '' });

  // Inline add-band selector (per user)
  const [addBandFor, setAddBandFor] = useState<number | null>(null);
  // Expanded detail rows
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    if (!isSuper) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiGetAppUsers(),
      apiGetBands(),
      apiGetInvitations().catch(() => [] as InvitationDTO[]),
      apiGetAllPeople().catch(() => [] as PersonBrief[]),
    ])
      .then(([u, b, i, p]) => {
        if (cancelled) return;
        setUsers(u); setBands(b); setInvitations(i); setPeople(p); setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isSuper, reloadKey]);

  // Audit log is paginated + filtered server-side (action / actor). Re-fetch
  // page 0 whenever the filters change so pagination respects them.
  useEffect(() => {
    if (!isSuper) return;
    let cancelled = false;
    apiGetAuditLog(AUDIT_PAGE, 0, auditAction, auditActor)
      .then((a) => { if (!cancelled) { setAudit(a); setAuditOffset(0); } })
      .catch(() => { if (!cancelled) setAudit([]); });
    return () => { cancelled = true; };
  }, [isSuper, auditAction, auditActor, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Protect the last active superadmin from self-lockout in the UI.
  const activeSuperadmins = useMemo(
    () => users.filter((u) => u.global_role === 'superadmin' && u.is_active).length,
    [users],
  );
  const isLastSuper = (u: AppUser) => u.global_role === 'superadmin' && u.is_active && activeSuperadmins <= 1;
  const isMe = (u: AppUser) => u.id === user?.id;

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!writeGate.canWrite) return;
    setError(null);
    try {
      await apiCreateAppUser(nu);
      setNu({ username: '', password: '', email: '', display_name: '', global_role: 'user' });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const setBandRole = async (u: AppUser, bandId: string, role: BandRole | '') => {
    if (!writeGate.canWrite) return;
    setError(null);
    try {
      if (role === '') await apiRemoveBandMember(bandId, u.id);
      else await apiSetBandMember(bandId, u.id, role);
      setAddBandFor(null);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update access');
      reportSaveError('team', err);
    }
  };

  const toggleActive = async (u: AppUser) => {
    if (!writeGate.canWrite) return;
    if (isLastSuper(u)) { setError(t('users.cannotDisableLast')); return; }
    setError(null);
    try {
      await apiUpdateAppUser(u.id, { is_active: !u.is_active });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const setGlobalRole = async (u: AppUser, global_role: GlobalRole) => {
    if (!writeGate.canWrite) return;
    if (isLastSuper(u) && global_role !== 'superadmin') {
      setError(t('users.cannotDemoteLast'));
      return;
    }
    if (global_role === 'superadmin' && u.id !== user?.id) {
      if (!(await showConfirm(`Grant platform superadmin to "${u.username}"? They get full control of every band and all accounts.`))) return;
    }
    setError(null);
    try {
      await apiUpdateAppUser(u.id, { global_role });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const linkPerson = async (u: AppUser, teamMemberId: string) => {
    if (!writeGate.canWrite) return;
    setError(null);
    try {
      await apiUpdateAppUser(u.id, { team_member_id: teamMemberId || null });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link person');
    }
  };

  const signOutEverywhere = async (u: AppUser) => {
    if (!writeGate.canWrite) return;
    if (!(await showConfirm(`Sign "${u.username}" out of every device? All their current sessions end immediately.`))) return;
    setError(null);
    try {
      await apiRevokeUserSessions(u.id);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke sessions');
    }
  };

  const unlockAccount = async (u: AppUser) => {
    if (!writeGate.canWrite) return;
    setError(null);
    try {
      await apiUnlockUser(u.id);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlock account');
    }
  };

  const submitPassword = async () => {
    if (!writeGate.canWrite) return;
    if (!pwModal) return;
    if (strength(pwVal).score < 2) { setError(t('users.passwordTooWeak')); return; }
    setError(null);
    try {
      await apiUpdateAppUser(pwModal.id, { password: pwVal });
      setPwModal(null); setPwVal('');
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    }
  };

  // Phase alpha: CREATE the account of a member without one by setting a
  // password on the person. 409 (already has an account) can't normally happen
  // — the button only shows for has_account=false — but it is surfaced anyway.
  const submitSetPassword = async () => {
    if (!writeGate.canWrite || !spPerson) return;
    if (strength(spVal).score < 2) { setError(t('users.passwordTooWeak')); return; }
    setError(null);
    try {
      await apiSetMemberPassword({ team_member_id: spPerson.id, password: spVal, must_change_password: spMcp });
      setSpPerson(null); setSpVal(''); setSpMcp(true);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    }
  };

  const openEdit = (u: AppUser) => {
    setEv({ username: u.username, email: u.email || '', display_name: u.display_name || '' });
    setEditModal(u);
  };

  const submitEdit = async () => {
    if (!writeGate.canWrite) return;
    if (!editModal) return;
    const uname = ev.username.trim();
    if (!uname || /\s/.test(uname)) { setError(t('users.usernameInvalid')); return; }
    setError(null);
    try {
      await apiUpdateAppUser(editModal.id, { username: uname, email: ev.email, display_name: ev.display_name });
      setEditModal(null);
      reload();
    } catch (err) {
      // 409 = username already taken by another account.
      if (err instanceof ApiError && err.status === 409) setError(t('users.usernameTaken'));
      else setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const removeUser = async (u: AppUser) => {
    if (!writeGate.canWrite) return;
    if (isLastSuper(u)) { setError(t('users.cannotDeleteLast')); return; }
    if (!(await showConfirm(`Delete the account "${u.username}"?\n\nTheir band access is removed. Their Team record (identity, passports) is NOT deleted.`))) return;
    setError(null);
    try {
      await apiDeleteAppUser(u.id);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!writeGate.canWrite) return;
    setError(null);
    setLastInvite(null);
    try {
      const res = await apiCreateInvitation({
        email: inv.email,
        band_id: inv.band_id || undefined,
        band_role: inv.band_role,
      });
      // The link is now persisted server-side (token_raw) and shown on the
      // pending row — this box confirms the delivery + gives the link.
      setLastInvite({ email: res.email, link: res.invite_link, delivery: res.delivery });
      setInv({ email: '', band_id: '', band_role: 'viewer' });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invitation');
    }
  };

  const regenerateInvite = async (i: InvitationDTO) => {
    if (!writeGate.canWrite) return;
    setError(null);
    setLastInvite(null);
    try {
      const res = await apiCreateInvitation({
        email: i.email,
        band_id: i.band_id || undefined,
        band_role: i.band_role,
      });
      setLastInvite({ email: res.email, link: res.invite_link, delivery: res.delivery });
      setTab('invitations');
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create a new link');
    }
  };

  const resendInvite = async (i: InvitationDTO) => {
    if (!writeGate.canWrite) return;
    setError(null);
    setLastInvite(null);
    try {
      const res = await apiResendInvitation(i.id);
      setLastInvite({ email: res.email, link: res.invite_link, delivery: res.delivery });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend the invitation');
    }
  };

  const loadMoreAudit = async () => {
    const next = auditOffset + AUDIT_PAGE;
    try {
      const more = await apiGetAuditLog(AUDIT_PAGE, next, auditAction, auditActor);
      setAudit((prev) => [...prev, ...more]);
      setAuditOffset(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more audit');
    }
  };

  if (!isSuper) {
    return (
      <div className="page">
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('users.notAvailable')}</h2>
          <p style={{ color: 'var(--text-dim)' }}>
            {t('users.notAvailableBody')}
          </p>
        </div>
      </div>
    );
  }

  // ---- derived: filtered + sorted users ----
  const q = search.trim().toLowerCase();
  const filteredUsers = users
    .filter((u) => {
      if (q && !(u.username.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.display_name || '').toLowerCase().includes(q))) return false;
      if (fRole && u.global_role !== fRole) return false;
      if (fStatus === 'active' && !u.is_active) return false;
      if (fStatus === 'disabled' && u.is_active) return false;
      if (fBand && !u.bands?.some((b) => b.id === fBand)) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortKey === 'last_login') return (b.last_login_at || '').localeCompare(a.last_login_at || '');
      if (sortKey === 'created') return (b.created_at || '').localeCompare(a.created_at || '');
      return a.username.localeCompare(b.username);
    });

  const filteredAudit = audit.filter((a) =>
    (!auditAction || a.action.toLowerCase().includes(auditAction.toLowerCase()))
    && (!auditActor || (a.username || '').toLowerCase().includes(auditActor.toLowerCase()))
  );

  const personName = (id?: string | null) => {
    const p = people.find((x) => x.id === id);
    return p ? (p.nickname || p.name) : null;
  };

  // ---- derived: the Team registry (all people, not the logins) ----
  // Both counters come from the payload, never a hard-coded number.
  const peopleWithoutAccount = people.filter((p) => !p.has_account).length;
  // Band options offered by the registry filter: only bands that actually hold
  // a registry person, so no option can yield an empty list.
  const registryBandOptions = (() => {
    const seen = new Map<string, string>();
    for (const p of people) for (const b of p.bands || []) if (!seen.has(b.id)) seen.set(b.id, b.name);
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  })();
  const regQ = regSearch.trim().toLowerCase();
  const filteredRegistry = people
    .filter((p) => {
      const label = (p.nickname || p.name || '').toLowerCase();
      if (regQ && !(label.includes(regQ) || (p.name || '').toLowerCase().includes(regQ))) return false;
      if (regBand && !(p.bands || []).some((b) => b.id === regBand)) return false;
      // The « no account » filter: a person WITH an account is never shown here.
      if (regNoAccountOnly && p.has_account) return false;
      return true;
    })
    .sort((a, b) => {
      if (regSort === 'category') {
        const c = (a.category || '').localeCompare(b.category || '');
        if (c) return c;
      }
      return (a.nickname || a.name || '').localeCompare(b.nickname || b.name || '');
    });

  // Prefill the EXISTING invitation form (email + roster band + default viewer
  // role) and reveal it on the Invitations tab. NOTHING is sent here — the admin
  // still presses "Create link" explicitly.
  const prefillInvite = (p: PersonBrief, bandId: string) => {
    setInv({ email: p.email || '', band_id: bandId || '', band_role: 'viewer' });
    setLastInvite(null);
    setRegInviteFor(null);
    setTab('invitations');
  };
  const startInvite = (p: PersonBrief) => {
    if (!p.email) return; // the button is disabled in that case
    const bands = p.bands || [];
    // Ambiguous roster (several bands) → ask, never guess.
    if (bands.length > 1) { setRegInviteFor(p.id); setRegInviteBand(bands[0].id); return; }
    prefillInvite(p, bands[0]?.id || '');
  };

  const USERS_CARDS: StackCard[] = [
    // LOT E1 (2026-11-02): `hasData` = the card has real content to show, so
    // it opens itself on a fresh view. `add` (account-creation form) and
    // `filters` (a tool) stay `false` — `filters` keeps opening through
    // `defaultOpen` below, which is the point of a tool card.
    { id: 'add', hasData: false, title: t('users.addAccount'), sub: t('users.addAccountHint'), node: (
<div>
  <form onSubmit={createUser} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
    <div style={{ flex: 1, minWidth: 140 }}>
      <label>{t('users.username')}</label>
      <input className="form-input" value={nu.username} required
        onChange={(e) => setNu({ ...nu, username: e.target.value })} />
    </div>
    <div style={{ flex: 1, minWidth: 140 }}>
      <label>{t('users.password')}</label>
      <PasswordInput className="form-input" value={nu.password} required
        onChange={(e) => setNu({ ...nu, password: e.target.value })} />
    </div>
    <div style={{ flex: 1, minWidth: 140 }}>
      <label>{t('users.email')}</label>
      <input className="form-input" type="text" value={nu.email}
        onChange={(e) => setNu({ ...nu, email: e.target.value })} />
    </div>
    <div style={{ flex: 1, minWidth: 140 }}>
      <label>{t('users.displayName')}</label>
      <input className="form-input" type="text" value={nu.display_name}
        onChange={(e) => setNu({ ...nu, display_name: e.target.value })} />
    </div>
    <div style={{ width: 150 }}>
      <label>{t('users.platformRole')}</label>
      <select className="form-input" value={nu.global_role}
        onChange={(e) => setNu({ ...nu, global_role: e.target.value as GlobalRole })}>
        <option value="user">{t('users.user')}</option>
        <option value="superadmin">{t('users.superadmin')}</option>
      </select>
    </div>
    <button className="btn btn-primary" type="submit" disabled={!writeGate.canWrite}>{t('users.create')}</button>
  </form>
</div>
    ) },
    { id: 'filters', hasData: false, title: t('users.filters'), node: (
<div>
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
    <div style={{ flex: '2 1 200px' }}>
      <label>{t('users.search')}</label>
      <input className="form-input" placeholder={t('users.searchPlaceholder')}
        value={search} onChange={(e) => setSearch(e.target.value)} />
    </div>
    <div style={{ width: 150 }}>
      <label>{t('users.platformRole')}</label>
      <select className="form-input" value={fRole} onChange={(e) => setFRole(e.target.value)}>
        <option value="">{t('users.all')}</option>
        <option value="user">{t('users.user')}</option>
        <option value="superadmin">{t('users.superadmin')}</option>
      </select>
    </div>
    <div style={{ width: 130 }}>
      <label>{t('users.status')}</label>
      <select className="form-input" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
        <option value="">{t('users.all')}</option>
        <option value="active">{t('users.active')}</option>
        <option value="disabled">{t('users.disabled')}</option>
      </select>
    </div>
    <div style={{ width: 170 }}>
      <label>{t('users.bandAccess')}</label>
      <select className="form-input" value={fBand} onChange={(e) => setFBand(e.target.value)}>
        <option value="">{t('users.anyBand')}</option>
        {bands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    </div>
    <div style={{ width: 150 }}>
      <label>{t('users.sortBy')}</label>
      <select className="form-input" value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
        <option value="username">{t('users.username')}</option>
        <option value="last_login">{t('users.lastLogin')}</option>
        <option value="created">{t('users.created')}</option>
      </select>
    </div>
  </div>
</div>
    ) },
    { id: 'table', hasData: filteredUsers.length > 0, title: `${t('users.accountsTitle')} (${filteredUsers.length})`, node: (
<div data-users-table>
<div className="table-wrapper">
  <table className="tbl-dense">
    <thead>
      <tr>
        <th style={{ width: '44%' }}>{t('users.colUser')}</th>
        <th>{t('users.colPlatform')}</th>
        <th>{t('users.col2fa')}</th>
        <th>{t('users.colStatus')}</th>
        <th>{t('users.lastLogin')}</th>
        <th style={{ width: 40 }}></th>
      </tr>
    </thead>
    <tbody>
      {filteredUsers.length === 0 && (
        <tr><td colSpan={6} className="text-dim" style={{ textAlign: 'center', padding: 20 }}>{t('users.noAccountsMatch')}</td></tr>
      )}
      {filteredUsers.map((u) => {
        const linkedName = personName(u.team_member_id);
        const unlinkedBands = bands.filter((b) => !u.bands?.some((x) => x.id === b.id));
        const locked = isLastSuper(u);
        const isOpen = expanded.has(u.id);
        const initials = ((u.display_name || u.username).trim()[0] || '?').toUpperCase();
        return (
          <Fragment key={u.id}>
          <tr style={{ opacity: u.is_active ? 1 : 0.5, cursor: 'pointer' }} onClick={() => toggleExpanded(u.id)}>
            <td>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="u-avatar">{initials}</span>
                <div>
                  <strong>{u.username}</strong>
                  {u.display_name && <div style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>{u.display_name}</div>}
                  {u.email && <div style={{ color: 'var(--text-dim)', fontSize: '0.78rem' }}>{u.email}</div>}
                  {u.bands && u.bands.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                      {u.bands.map((b) => (
                        <span key={b.id} className="u-bandtag">{b.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </td>
            <td>
              <span className={`u-pill ${u.global_role === 'superadmin' ? 'u-pill--super' : 'u-pill--user'}`}>
                {u.global_role === 'superadmin' ? t('users.superadmin') : t('users.user')}
              </span>
              {locked && <div className="text-dim" style={{ fontSize: '0.72rem', marginTop: 4 }}>{t('users.lastSuperadmin')}</div>}
            </td>
            <td>
              {u.has_2fa ? (
                <span style={{ color: 'var(--success)', fontSize: '0.85rem' }}>✓ 2FA</span>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>—</span>
              )}
            </td>
            <td>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span className={`u-dot ${u.is_active ? 'u-dot--on' : 'u-dot--off'}`} />
                <span style={{ color: u.is_active ? 'var(--success)' : 'var(--text-muted)', fontSize: '0.85rem' }}>
                  {u.is_active ? t('users.active') : t('users.disabled')}
                </span>
              </span>
              {u.login_locked && (
                <div style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: 4 }}>
                  🔒 {t('users.locked')}{u.login_failures != null && ` (${u.login_failures})`}
                </div>
              )}
            </td>
            <td style={{ fontSize: '0.82rem', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
              {u.last_login_at ? u.last_login_at.slice(0, 16) : t('users.never')}
            </td>
            <td>
              <button className="u-chevr"
                onClick={(e) => { e.stopPropagation(); toggleExpanded(u.id); }}
                title={isOpen ? t('users.collapseDetails') : t('users.manageAccess')}>
                {isOpen ? '▾' : '▸'}
              </button>
            </td>
          </tr>
          {isOpen && (
            <tr className="u-detail">
              <td colSpan={6}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 18 }}>
                  <div>
                    <div className="u-sectionlabel">{t('users.bandAccess')}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                      {u.bands?.length ? u.bands.map((b) => (
                        <span key={b.id} className="u-bandchip" title={u.global_role === 'superadmin'
                          ? 'Superadmins can access every band by default — an explicit link here just makes them the named owner/editor/viewer on this band.'
                          : t(ROLE_HELP[(b.role || 'viewer') as BandRole])}>
                          <span>{b.name}</span>
                          <select value={b.role} disabled={!writeGate.canWrite}
                            onChange={(e) => setBandRole(u, b.id, e.target.value as BandRole | '')}>
                            {BAND_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                          <button className="u-bandchip__x" title={t('users.removeAccess')} disabled={!writeGate.canWrite} onClick={() => setBandRole(u, b.id, '')}>×</button>
                        </span>
                      )) : <span className="text-dim" style={{ fontSize: '0.85rem' }}>{t('users.noBandAccess')}</span>}
                    </div>
                    {addBandFor === u.id ? (
                      <select className="form-input" style={{ width: 200 }} value="" autoFocus
                        onChange={(e) => { if (e.target.value) setBandRole(u, e.target.value, 'viewer'); }}>
                        <option value="">{t('users.pickBand')}</option>
                        {unlinkedBands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    ) : (
                      <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => setAddBandFor(addBandFor === u.id ? null : u.id)}>{t('users.addBand')}</button>
                    )}
                  </div>
                  <div>
                    <div className="u-sectionlabel">{t('users.linkedPerson')}</div>
                    <select className="form-input" style={{ width: '100%', maxWidth: 360 }}
                      value={u.team_member_id || ''}
                      disabled={!writeGate.canWrite}
                      title={linkedName ? t('users.linkedTo', { name: linkedName }) : t('users.linkToPerson')}
                      onChange={(e) => linkPerson(u, e.target.value)}>
                      <option value="">{t('users.none')}</option>
                      {people.map((p) => (<option key={p.id} value={p.id}>{p.nickname || p.name}</option>))}
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 16, borderTop: '1px solid var(--divider)', paddingTop: 14 }}>
                  <div className="u-sectionlabel" style={{ marginBottom: 8 }}>{t('users.actions')}</div>
                  <div className="u-rowactions">
                    {u.global_role === 'superadmin' ? (
                      <button className="btn btn-small" disabled={locked || !writeGate.canWrite} title={locked ? t('users.lastSuperCannotDemote') : t('users.downgradeToUser')}
                        onClick={() => setGlobalRole(u, 'user')}>{t('users.demote')}</button>
                    ) : (
                      <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => setGlobalRole(u, 'superadmin')}>{t('users.promote')}</button>
                    )}
                    <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => { setPwModal(u); setPwVal(''); }}>{t('users.passwordBtn')}</button>
                    <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => openEdit(u)}>{t('users.editAccount')}</button>
                    <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => signOutEverywhere(u)}>{t('users.signOut')}</button>
                    {u.login_locked && (
                      <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => unlockAccount(u)}>{t('users.unlock')}</button>
                    )}
                    <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => toggleActive(u)}>
                      {u.is_active ? t('users.disable') : t('users.enable')}
                    </button>
                    {!isMe(u) && (
                      <button className="btn btn-small btn-danger" disabled={locked || !writeGate.canWrite} onClick={() => removeUser(u)}>{t('users.delete')}</button>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          )}
          </Fragment>
        );
      })}
    </tbody>
  </table>
</div>
  </div>
    ) },
    { id: 'registry', hasData: people.length > 0, title: t('users.registryTitle'), node: (
<div data-team-registry>
  <p style={{ marginTop: 8 }}>
    {t('users.registrySummary', {
      people: people.length,
      withoutAccount: peopleWithoutAccount,
      accounts: users.length,
    })}
  </p>
  <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginTop: -6 }}>
    {t('users.registryNote')}
  </p>
  {/* Computed counters, exposed off-screen for tests/screen readers. */}
  <span data-testid="registry-count-people" style={{ position: 'absolute', left: -9999 }}>{people.length}</span>
  <span data-testid="registry-count-no-account" style={{ position: 'absolute', left: -9999 }}>{peopleWithoutAccount}</span>

  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', margin: '10px 0 14px' }}>
    <div style={{ flex: '2 1 200px' }}>
      <label>{t('users.registrySearch')}</label>
      <input className="form-input" placeholder={t('users.registrySearchPh')}
        value={regSearch} onChange={(e) => setRegSearch(e.target.value)} />
    </div>
    <div style={{ width: 180 }}>
      <label>{t('users.registryBand')}</label>
      <select className="form-input" value={regBand} onChange={(e) => setRegBand(e.target.value)}>
        <option value="">{t('users.registryAllBands')}</option>
        {registryBandOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    </div>
    <div style={{ width: 150 }}>
      <label>{t('users.registrySort')}</label>
      <select className="form-input" value={regSort}
        onChange={(e) => setRegSort(e.target.value as 'name' | 'category')}>
        <option value="name">{t('users.registrySortName')}</option>
        <option value="category">{t('users.registrySortCategory')}</option>
      </select>
    </div>
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', paddingBottom: 6 }}>
      <input type="checkbox" checked={regNoAccountOnly}
        onChange={(e) => setRegNoAccountOnly(e.target.checked)} />
      {t('users.registryNoAccountOnly')}
    </label>
  </div>

  <div className="table-wrapper">
    <table className="tbl-dense">
      <thead>
        <tr>
          <th style={{ width: '40%' }}>{t('users.colUser')}</th>
          <th>{t('users.registryBand')}</th>
          <th>{t('users.colStatus')}</th>
          <th style={{ width: 240 }}></th>
        </tr>
      </thead>
      <tbody>
        {filteredRegistry.length === 0 && (
          <tr><td colSpan={4} className="text-dim" style={{ textAlign: 'center', padding: 20 }}>{t('users.registryEmpty')}</td></tr>
        )}
        {filteredRegistry.map((p) => {
          const display = p.nickname || p.name;
          const bands = p.bands || [];
          const noEmail = !p.email;
          const inviteTitle = noEmail ? t('users.registryNoEmail')
            : bands.length > 1 ? t('users.registryPickBand') : t('users.registryInvite');
          return (
            <tr key={p.id} data-registry-person={p.id} style={{ opacity: p.has_account ? 0.78 : 1 }}>
              <td>
                <strong>{display}</strong>
                {!!p.nickname && p.nickname !== p.name && (
                  <span style={{ color: 'var(--text-dim)', fontSize: '0.78rem' }}> · {p.name}</span>
                )}
                {p.category && <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>{p.category}</div>}
                {p.email && <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>{p.email}</div>}
              </td>
              <td>
                {bands.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {bands.map((b) => <span key={b.id} className="u-bandtag">{b.name}</span>)}
                  </div>
                ) : <span className="text-dim" style={{ fontSize: '0.82rem' }}>{t('users.noBandAccess')}</span>}
              </td>
              <td>
                {p.has_account ? (
                  <span className="u-pill u-pill--user" data-registry-badge="account">
                    {t('users.registryHasAccount', { username: p.account?.username || '' })}
                  </span>
                ) : (
                  <span className="u-pill u-pill--user" data-registry-badge="no-account"
                    style={{ background: 'color-mix(in srgb, var(--danger) 16%, transparent)', color: 'var(--danger)' }}>
                    {t('users.registryNoAccount')}
                  </span>
                )}
              </td>
              <td>
                <div className="u-rowactions" style={{ justifyContent: 'flex-end' }}>
                  {/* Phase alpha: without an account → "Set password"
                      CREATES it; with an account → reuses the existing
                      "Reset password" modal (same one as Accounts). */}
                  {!p.has_account ? (
                    <button className="btn btn-small btn-primary"
                      data-registry-setpw={p.id}
                      disabled={!writeGate.canWrite}
                      title={t('users.setPwTitle', { name: display })}
                      onClick={() => { setSpPerson(p); setSpVal(''); setSpMcp(true); }}>
                      {t('users.setPwBtn')}
                    </button>
                  ) : (
                    <button className="btn btn-small"
                      data-registry-resetpw={p.id}
                      disabled={!writeGate.canWrite}
                      title={t('users.registryResetPw')}
                      onClick={() => {
                        const u = users.find((x) => x.id === p.account?.id);
                        if (u) { setPwModal(u); setPwVal(''); }
                      }}>
                      {t('users.registryResetPw')}
                    </button>
                  )}
                  {regInviteFor === p.id ? (
                    <>
                      <select className="form-input" style={{ width: 150 }} value={regInviteBand}
                        autoFocus onChange={(e) => setRegInviteBand(e.target.value)}>
                        {bands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                      <button className="btn btn-small btn-primary" disabled={!writeGate.canWrite}
                        onClick={() => prefillInvite(p, regInviteBand)}>{t('users.registryInvite')}</button>
                      <button className="btn btn-small" onClick={() => setRegInviteFor(null)}>{t('users.cancel')}</button>
                    </>
                  ) : (
                    <button className="btn btn-small"
                      data-registry-invite={p.id}
                      disabled={!writeGate.canWrite || noEmail}
                      title={inviteTitle}
                      onClick={() => startInvite(p)}>{t('users.registryInvite')}</button>
                  )}
                  <Link className="btn btn-small" to={`/tour/team/${p.id}`}>{t('users.registryOpenPerson')}</Link>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
</div>
    ) },
  ];

  return (
    <div ref={denseRef} className="page" data-page="users">
      <PageHeader
        title={withPageIcon('users.title', t('users.title'))}
        subtitle={
          <>
            <div>{t(activeSuperadmins > 1 ? 'users.subtitle_other' : 'users.subtitle', { count: activeSuperadmins })}</div>
            {showcase && <div>{t('showcase.adminReadOnly')}</div>}
          </>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['users', 'invitations', 'audit'] as Tab[]).map((tabKey) => (
          <button key={tabKey}
            className={`btn btn-small ${tab === tabKey ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(tabKey)}>
            {tabKey === 'users' ? t('users.tabAccounts', { count: users.length })
              : tabKey === 'invitations' ? t('users.tabInvitations', { count: invitations.length })
              : t('users.tabAudit')}
          </button>
        ))}
      </div>

      {error && (
        <div className="card" style={{ borderColor: 'var(--danger)', marginBottom: 18 }}>
          <strong style={{ color: 'var(--danger)' }}>{error}</strong>
        </div>
      )}

      {loading && <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>}

      {/* ---------------- ACCOUNTS ---------------- */}
      {!loading && tab === 'users' && (
        <>
          <CardStack pageId="users" cards={USERS_CARDS} foldable defaultOpen={['filters']} />
        </>
      )}

      {/* ---------------- INVITATIONS ---------------- */}
      {!loading && tab === 'invitations' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ marginTop: 0 }}>{t('users.inviteSomeone')}</h3>
            <form onSubmit={sendInvite} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label>{t('users.email')}</label>
                <input className="form-input" type="text" value={inv.email} required
                  onChange={(e) => setInv({ ...inv, email: e.target.value })} />
              </div>
              <div style={{ width: 180 }}>
                <label>{t('users.band')}</label>
                <select className="form-input" value={inv.band_id}
                  onChange={(e) => setInv({ ...inv, band_id: e.target.value })}>
                  <option value="">{t('users.noBand')}</option>
                  {bands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div style={{ width: 130 }}>
                <label>{t('users.role')}</label>
                <select className="form-input" value={inv.band_role}
                  onChange={(e) => setInv({ ...inv, band_role: e.target.value as BandRole })}>
                  {BAND_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button className="btn btn-primary" type="submit" disabled={!writeGate.canWrite}>{t('users.createLink')}</button>
            </form>

            {lastInvite && (
              <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--success)', borderRadius: 8 }}>
                <strong style={{ color: 'var(--success)' }}>{t('users.inviteSentTitle')}</strong>
                <p style={{ color: 'var(--text-dim)', fontSize: '0.84rem', margin: '4px 0 8px' }}>
                  {t('users.inviteSentTo', { email: lastInvite.email })}{' '}
                  {lastInvite.delivery?.sent
                    ? (lastInvite.delivery.mode === 'smtp'
                        ? t('users.inviteModeSmtp')
                        : t('users.inviteModeFile'))
                    : t('users.inviteSendError', { error: lastInvite.delivery?.error || 'unknown' })}
                </p>
                <input className="form-input" readOnly value={lastInvite.link}
                  onFocus={(e) => e.currentTarget.select()} />
                <button className="btn btn-small" style={{ marginTop: 8 }}
                  onClick={() => copyText(lastInvite.link)}>
                  {copied ? t('users.copied') : t('users.copy')}
                </button>
                <p style={{ color: 'var(--text-dim)', fontSize: '0.78rem', margin: '8px 0 0' }}>
                  {t('users.inviteLinkDurableHint')}
                </p>
              </div>
            )}
          </div>

          {invitations.length === 0 ? (
            <p style={{ color: 'var(--text-dim)' }}>{t('users.noPendingInvitation')}</p>
          ) : (
            <div className="table-wrapper">
              <table className="tbl-dense">
                <thead>
                  <tr><th>{t('users.email')}</th><th>{t('users.band')}</th><th>{t('users.role')}</th><th>{t('users.colStatus')}</th><th>{t('users.colExpires')}</th><th>{t('users.inviteLinkCol')}</th><th></th></tr>
                </thead>
                <tbody>
                  {invitations.map((i) => {
                    const now = Date.now();
                    const expired = i.expires_at < now;
                    const daysLeft = Math.ceil((i.expires_at - now) / 86400000);
                    return (
                      <tr key={i.id}>
                        <td>{i.email}</td>
                        <td>{i.band_name || '—'}</td>
                        <td>{i.band_role}</td>
                        <td>
                          {expired
                            ? <span style={{ color: 'var(--danger)' }}>{t('users.expired')}</span>
                            : <span style={{ color: 'var(--success)' }}>{t('users.pending')}</span>}
                        </td>
                        <td style={{ fontSize: '0.84rem', color: 'var(--text-dim)' }}>
                          {new Date(i.expires_at).toLocaleDateString()}
                          {!expired && <span> {t('users.daysLeft', { count: daysLeft })}</span>}
                        </td>
                        <td>
                          {i.invite_link ? (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              <input className="form-input" readOnly value={i.invite_link}
                                style={{ minWidth: 220, fontSize: '0.78rem' }}
                                onFocus={(e) => e.currentTarget.select()} />
                              <button className="btn btn-small" onClick={() => copyText(i.invite_link!)}>
                                {copied ? t('users.copied') : t('users.copy')}
                              </button>
                            </div>
                          ) : <span className="text-dim" style={{ fontSize: '0.8rem' }}>—</span>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-small"
                              disabled={!writeGate.canWrite || expired || !i.invite_link}
                              title={t('users.resendConfirm', { email: i.email })}
                              onClick={() => resendInvite(i)}>{t('users.resend')}</button>
                            <button className="btn btn-small" disabled={!writeGate.canWrite} onClick={() => regenerateInvite(i)}>{t('users.newLink')}</button>
                            <button className="btn btn-small btn-danger"
                              disabled={!writeGate.canWrite}
                              onClick={async () => { await apiRevokeInvitation(i.id); reload(); }}>
                              {t('users.revoke')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ---------------- AUDIT ---------------- */}
      {!loading && tab === 'audit' && (
        <>
          <div className="card" style={{ marginBottom: 20, padding: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label>{t('users.filterByAction')}</label>
                <input className="form-input" placeholder={t('users.filterByActionPh')}
                  value={auditAction} onChange={(e) => setAuditAction(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <label>{t('users.filterByActor')}</label>
                <input className="form-input" placeholder={t('users.actorPh')}
                  value={auditActor} onChange={(e) => setAuditActor(e.target.value)} />
              </div>
              <button className="btn btn-small" onClick={() => { setAuditAction(''); setAuditActor(''); }}>{t('users.clear')}</button>
            </div>
          </div>

          {filteredAudit.length === 0 ? (
            <p style={{ color: 'var(--text-dim)' }}>{t('users.noEntriesMatch')}</p>
          ) : (
            <div className="table-wrapper">
              <table className="tbl-dense">
                <thead>
                  <tr><th>{t('users.colWhen')}</th><th>{t('users.colWho')}</th><th>{t('users.colAction')}</th><th>{t('users.colTarget')}</th><th>{t('users.colDetail')}</th></tr>
                </thead>
                <tbody>
                  {filteredAudit.map((a) => (
                    <tr key={a.id}>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{a.created_at}</td>
                      <td>{a.username || '—'}</td>
                      <td><code style={{ fontSize: '0.82rem' }}>{a.action}</code></td>
                      <td style={{ fontSize: '0.82rem' }}>{a.target_type} {a.target_id?.slice(0, 8)}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-dim)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {a.detail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-small" onClick={loadMoreAudit}>{t('users.loadMore')}</button>
          </div>
        </>
      )}

      {/* ---------------- Password reset modal ---------------- */}
      {pwModal && (
        <div className="modal-overlay" onClick={() => setPwModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('users.setPasswordFor', { username: pwModal.username })}</h3>
              <button type="button" className="modal-close" aria-label={t('users.close')} onClick={() => setPwModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <label>{t('users.newPassword')}</label>
              <PasswordInput className="form-input" autoFocus value={pwVal}
                onChange={(e) => setPwVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void submitPassword(); }} />
              {pwVal && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} style={{ height: 5, flex: 1, borderRadius: 3, background: i < strength(pwVal).score ? STRENGTH_COLORS[strength(pwVal).score] : 'color-mix(in srgb, var(--text-muted) 20%, transparent)' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 'var(--font-xs)', color: STRENGTH_INK[strength(pwVal).score] }}>{strength(pwVal).label}</span>
                </div>
              )}
              <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginTop: 8 }}>
                {t('users.pwHint', { username: pwModal.username })}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPwModal(null)}>{t('users.cancel')}</button>
              <button className="btn btn-primary" disabled={!writeGate.canWrite} onClick={() => void submitPassword()}>{t('users.setPassword')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- "Set password" modal (creates the account) ---------------- */}
      {spPerson && (
        <div className="modal-overlay" onClick={() => setSpPerson(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('users.setPwTitle', { name: spPerson.nickname || spPerson.name })}</h3>
              <button type="button" className="modal-close" aria-label={t('users.close')} onClick={() => setSpPerson(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginTop: 0 }}>
                {t('users.setPwCreates', { username: spPerson.nickname || spPerson.name })}
              </p>
              <label>{t('users.newPassword')}</label>
              <PasswordInput className="form-input" autoFocus value={spVal}
                onChange={(e) => setSpVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void submitSetPassword(); }} />
              {spVal && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} style={{ height: 5, flex: 1, borderRadius: 3, background: i < strength(spVal).score ? STRENGTH_COLORS[strength(spVal).score] : 'color-mix(in srgb, var(--text-muted) 20%, transparent)' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 'var(--font-xs)', color: STRENGTH_INK[strength(spVal).score] }}>{strength(spVal).label}</span>
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: '0.88rem' }}>
                <input type="checkbox" checked={spMcp} onChange={(e) => setSpMcp(e.target.checked)} />
                {t('users.setPwMustChange')}
              </label>
              <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginTop: 8 }}>
                {t('users.setPwHint')}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSpPerson(null)}>{t('users.cancel')}</button>
              <button className="btn btn-primary" disabled={!writeGate.canWrite || strength(spVal).score < 2}
                onClick={() => void submitSetPassword()}>{t('users.setPwBtn')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Edit account modal (username / email / display name) ---------------- */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('users.editAccountTitle', { username: editModal.username })}</h3>
              <button type="button" className="modal-close" aria-label={t('users.close')} onClick={() => setEditModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <label>{t('users.username')}</label>
              <input className="form-input" autoFocus value={ev.username}
                onChange={(e) => setEv({ ...ev, username: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') void submitEdit(); }} />
              <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginTop: 4 }}>
                {t('users.usernameHint')}
              </p>
              <label style={{ marginTop: 12 }}>{t('users.email')}</label>
              <input className="form-input" type="text" value={ev.email}
                onChange={(e) => setEv({ ...ev, email: e.target.value })} />
              <label style={{ marginTop: 12 }}>{t('users.displayName')}</label>
              <input className="form-input" type="text" value={ev.display_name}
                onChange={(e) => setEv({ ...ev, display_name: e.target.value })} />
              <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginTop: 8 }}>
                {t('users.editHint')}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditModal(null)}>{t('users.cancel')}</button>
              <button className="btn btn-primary" disabled={!writeGate.canWrite} onClick={() => void submitEdit()}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
